// Instances — one deployed Sluice gateway each (`06` §3.4, `SC-F300`–`SC-F333`).
//
// The instance record is the **desired state**; Swarm is a projection of it
// (`SC-F300`). That is why [getInstanceStatus] returns desired *and* observed
// side by side instead of one fused truth: a paused Swarm update is invisible
// unless it is polled for, and `SC-F319` makes surfacing it an obligation.
//
// **There is no scale endpoint and there never will be.** `SC-F329` makes one
// replica per instance an invariant, not a default: a Sluice process owns its
// hash-chained audit JSONL and its approval and budget SQLite files
// exclusively, and two writers is corruption. Vertical sizing is the only
// capacity control and it is one knob, from which the container memory limit,
// DuckDB's `memory_limit`, `threads`, `Pids` and the reservations are all
// derived together. No configuration surface offers a replica count.

import { pb } from "./client.js";
import {
  del,
  getJSON,
  getWithETag,
  newIdempotencyKey,
  query,
  request,
  sendJSON,
  type Page,
  type PageParams,
} from "./http.js";
import type { Job, JobStarted } from "./jobs.js";

const BASE = "/v1/instances";

/**
 * InstanceState is the lifecycle state — `02` §2.1's eleven values verbatim.
 * `02` §2.3 owns the transitions and their timeouts; this is only the column.
 */
export type InstanceState =
  | "draft"
  | "provisioning"
  | "starting"
  | "healthy"
  | "degraded"
  | "updating"
  | "rolling-back"
  | "suspended"
  | "deleting"
  | "deleted"
  | "failed";

/** DesiredState is what the customer asked for; [InstanceState] is where we are. */
export type DesiredState = "running" | "suspended" | "deleted";

/**
 * InstanceSize is the single capacity knob (`SC-F309`). The dedicated tiers are
 * `SC-F310` (v1) and widen the enum in **response** positions only; `06` §1.3
 * makes a new enum member additive there and breaking in request positions.
 */
export type InstanceSize = "small" | "medium" | "large" | `dedicated_${string}`;

/**
 * AuditProfile is the instance's audit privacy profile (`SC-F821`; `SC-F822`
 * for `customer_held`). It is control-plane-owned, organization-owner-settable
 * and **restart-class**: choosing it at creation costs nothing, changing it
 * later restarts the instance. It is never set through [updateInstance].
 */
export type AuditProfile = "standard" | "minimized" | "customer_held";

/**
 * Instance is the desired state plus the summary a listing renders.
 *
 * `name` and `slug` are two different human-facing fields and must not be
 * confused: `name` is the organization-facing label, unique per organization
 * and freely renameable; `slug` is the single DNS label under the zone that
 * forms `hostname`, and it is **zone-globally** unique (`OD-08`): the slug alone
 * names the hostname, and the edge orders one certificate per hostname.
 */
export interface Instance {
  id: string;
  org: string;
  name: string;
  slug: string;
  display_name?: string;
  hostname: string;
  size: InstanceSize;
  sluice_version: string;
  /** `sha256:…`. Never customer-supplied: the digest, not the tag, is deployed. */
  image_digest: string;
  audit_profile: AuditProfile;
  desired_state: DesiredState;
  observed_state: InstanceState;
  /** The live generation number, or null before the first publish. */
  generation: number | null;
  created: string;
  updated?: string;
}

/** CreateInstanceInput is the provisioning body (`06` §3.4). */
export interface CreateInstanceInput {
  org: string;
  name: string;
  /**
   * Optional and client-*suggested*. Validated against
   * `^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$` and taken if free; derived from
   * `name` when absent. The server disambiguates on collision (`acme-prod`,
   * `acme-prod-2`) and returns what it actually assigned — a collision is not
   * an error, because a 409 on a hostname is a poor first experience for a
   * namespace the customer cannot see. **Render the returned slug**, never the
   * one that was sent.
   */
  slug?: string;
  size: InstanceSize;
  sluice_version: string;
  /** Defaults to `standard`. Accepted here because it is restart-class. */
  audit_profile?: AuditProfile;
  region_note?: string;
}

/** InstanceCreated is the `202 Accepted` body: the instance row as stored, plus
 * the provisioning job to watch with `watchJob`. */
export interface InstanceCreated extends JobStarted {
  instance: Instance;
}

/** listInstances lists instances the caller can see, self-filtered to a scoped
 * token's reach rather than refused (`routeList`). */
export function listInstances(
  params: {
    org?: string;
    status?: InstanceState;
    q?: string;
  } & PageParams = {},
): Promise<Page<Instance>> {
  return getJSON<Page<Instance>>(`${BASE}${query({ ...params })}`);
}

/** getInstance reads one instance's desired state and summary. */
export function getInstance(id: string): Promise<Instance> {
  return getJSON<Instance>(`${BASE}/${encodeURIComponent(id)}`);
}

/**
 * getInstanceWithETag is [getInstance] plus the record's `ETag`, for the settings
 * form that is going to `PATCH` it back.
 *
 * The tag is the record's own `updated` column, so it changes on every write —
 * including one made by another maintainer between this read and the save.
 * `06` §1.7 answers a stale tag with `412 precondition_failed` rather than
 * overwriting, which is the only reason a rename and a size change can share one
 * form without one of them silently reverting the other.
 */
export function getInstanceWithETag(
  id: string,
): Promise<{ value: Instance; etag: string }> {
  return getWithETag<Instance>(`${BASE}/${encodeURIComponent(id)}`);
}

/** createInstance provisions an instance (`SC-F300`, `SC-F308`) → job. Owner
 * only. The `Idempotency-Key` is required: a naive retry after a timeout must
 * not double-provision. */
export function createInstance(
  input: CreateInstanceInput,
): Promise<InstanceCreated> {
  return sendJSON<InstanceCreated>(BASE, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/** UpdateInstanceInput is the maintainer-level patch: display name, size
 * (`SC-F309`) and pinned Sluice version (`SC-F323`). Deliberately cannot carry
 * `audit_profile` — that is owner-level and restart-class, see
 * [setAuditProfile]. */
export interface UpdateInstanceInput {
  name?: string;
  display_name?: string;
  size?: InstanceSize;
  sluice_version?: string;
}

/**
 * updateInstance patches an instance. Answers the updated record for a cosmetic
 * change and `202` + a job when the change is material (a size or version change
 * is a rolling restart). Send the `ETag` you read as `ifMatch`.
 */
export function updateInstance(
  id: string,
  patch: UpdateInstanceInput,
  opts: { ifMatch?: string } = {},
): Promise<Instance | InstanceCreated> {
  return sendJSON<Instance | InstanceCreated>(
    `${BASE}/${encodeURIComponent(id)}`,
    "PATCH",
    patch,
    {
      ...opts,
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

/**
 * deleteInstance deprovisions with export and retention (`SC-F318`) → job.
 * Owner only, step-up gated at v1, and irreversible: it destroys the state
 * volume, the local audit segments not yet ingested and the approval store. The
 * slug stays quarantined for the whole retention window, so the hostname cannot
 * be handed to another organization (`07` D12).
 */
export function deleteInstance(
  id: string,
  opts: { verification?: string } = {},
): Promise<JobStarted> {
  return sendJSON<JobStarted>(
    `${BASE}/${encodeURIComponent(id)}`,
    "DELETE",
    {},
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

/**
 * restartInstance increments Swarm's `ForceUpdate` without mutating the spec
 * (`SC-F317`) → job. Deliberately **not** step-up gated: it is disruptive but
 * reversible and observable, and gating routine operations makes the gate noise.
 */
export function restartInstance(id: string): Promise<JobStarted> {
  return power(id, "restart");
}

/** suspendInstance scales to zero and keeps state (`SC-F317`) → job. Owner. */
export function suspendInstance(id: string): Promise<JobStarted> {
  return power(id, "suspend");
}

/** resumeInstance returns the instance to one replica (`SC-F317`) → job. Owner. */
export function resumeInstance(id: string): Promise<JobStarted> {
  return power(id, "resume");
}

/** upgradeInstance changes the Sluice version with rollback (`SC-F324`, v1) →
 * job. The target must be in the published digest allowlist or the answer is
 * `image_not_allowed` (`SC-F323`). */
export function upgradeInstance(
  id: string,
  sluiceVersion: string,
): Promise<JobStarted> {
  return sendJSON<JobStarted>(
    `${BASE}/${encodeURIComponent(id)}/upgrade`,
    "POST",
    { sluice_version: sluiceVersion },
    { idempotencyKey: newIdempotencyKey() },
  );
}

/** retryInstance runs the first publish again for an instance that never went
 * live (`SC-S300`) → a new `instance.create` job. The server refuses an
 * instance that had an active generation or has another open job. */
export function retryInstance(id: string): Promise<JobStarted> {
  return sendJSON<JobStarted>(
    `${BASE}/${encodeURIComponent(id)}/retry`,
    "POST",
    {},
    { idempotencyKey: newIdempotencyKey() },
  );
}

function power(
  id: string,
  action: "restart" | "suspend" | "resume",
): Promise<JobStarted> {
  return sendJSON<JobStarted>(
    `${BASE}/${encodeURIComponent(id)}/${action}`,
    "POST",
    {},
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * setAuditProfile changes the audit privacy profile (`SC-F821`) → job. Owner
 * level and restart-class, so the body carries an explicit acknowledgement of
 * the restart; without it the answer names the blast radius rather than
 * restarting the instance behind the operator's back.
 *
 * `customer_held` answers `not_implemented` until `SC-F822` lands — offer it
 * disabled and badged rather than letting it fail (`concept.md` §7 principle 8).
 */
export function setAuditProfile(
  id: string,
  profile: AuditProfile,
  opts: { allowRestart: boolean; verification?: string },
): Promise<JobStarted> {
  return sendJSON<JobStarted>(
    `${BASE}/${encodeURIComponent(id)}/audit-profile`,
    "PUT",
    { audit_profile: profile, allow_restart: opts.allowRestart },
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

// ──────────────────────────────────────────────── observed state ──

/** SwarmTask is one running container as Swarm reports it. */
export interface SwarmTask {
  id: string;
  slot: number;
  node: string;
  desired_state: string;
  state: string;
  since: string;
}

/**
 * UpdateStatus is Swarm's rollout state. `state` may be **`paused`**:
 * `RollbackConfig.FailureAction` accepts only `continue` or `pause`, never
 * `rollback`, so a failed rollback pauses and there is no second-order automatic
 * recovery. The UI must surface it — that obligation is `SC-F319`, and a paused
 * rollout rendered as "updating" is a silent outage.
 */
export interface UpdateStatus {
  state:
    | "updating"
    | "paused"
    | "completed"
    | "rollback_started"
    | "rollback_paused"
    | "rollback_completed";
  message: string;
  completed: string | null;
}

/**
 * VolumeReclamation is one node-local volume's teardown outcome (`SC-F916`).
 *
 * Swarm has no cluster-scoped volume API — `VolumeRemove` acts on the manager's
 * own filesystem, not on the pinned worker where the data lives (`02` §7.7) — so
 * the only thing that can confirm reclamation is `sc-reaper` on that node. Until
 * it does, nothing may claim the data is gone (`05` §6.4).
 */
export interface VolumeReclamation {
  /** `sc-v-<INST>-state`, `-bundle` or `-spill` (`02` §7.7). */
  volume: string;
  /** `refused` is the reaper declining to delete an unexported chain (`SC-S323`). */
  state: "pending" | "reclaimed" | "unreachable" | "refused";
  /** Bytes the reaper reported freeing, once it has reported any. */
  bytes: number | null;
  confirmed: string | null;
  /** Why it is not `reclaimed` yet. Rendered verbatim. */
  reason: string | null;
}

/**
 * ReclamationStatus is the deletion half of the truth (`SC-F916`, `SC-S323`).
 * Present only while an instance is `deleting` or `deleted`; null otherwise.
 *
 * `node` is not decoration: `SC-S323` requires the UI to say **which node** is
 * holding a reclamation that has not completed, because "deleted" is a statement
 * a customer repeats to an auditor.
 */
export interface ReclamationStatus {
  /** The pinned node whose disk holds the volumes. */
  node: string;
  node_reachable: boolean;
  volumes: VolumeReclamation[];
  /** When the record entered `deleting`; `02` §9.4 D12 alerts past 7 days. */
  deleting_since: string | null;
}

/** InstanceStatus is the honest desired-versus-observed view (`SC-F319`). */
export interface InstanceStatus {
  desired: {
    state: DesiredState;
    generation: number | null;
    image_digest: string;
    size: InstanceSize;
    /** The node label the create pinned the instance to. Not the observed node. */
    pinned_node?: string;
  };
  observed: {
    state: InstanceState;
    /** The server's SC-S317 verdict: the newest observation is older than
     * three reconciler passes for this state. */
    stale?: boolean;
    /** When that observation was taken; null when never observed. */
    observed_at?: string | null;
    generation: number | null;
    config_digest: string | null;
    replicas: { desired: number; running: number };
    update_status: UpdateStatus | null;
    tasks: SwarmTask[];
  };
  drift: { digest_match: boolean; checked: string };
  reconcile: ReconcileStatus;
  /** Present only while the instance is `deleting` or `deleted` (`SC-F916`). */
  reclamation?: ReclamationStatus | null;
}

/** ReconcileStatus is the reconcile loop's own liveness and verdict — a
 * SEPARATE axis from the lifecycle state, which is what makes "the instance is
 * healthy but nothing has checked recently" expressible (`00` §5.5).
 *
 * `state: "blocked"` is the one a screen must not swallow: automatic repair has
 * stopped, so nothing will change until a person acts. `error.message` is the
 * substrate's own text and is rendered verbatim (`02` §9.4 D11). */
export interface ReconcileStatus {
  last_pass: string | null;
  quarantined: boolean;
  state?: string;
  error?: { code: string; message: string } | null;
  consecutive_failures?: number;
  next_attempt?: string | null;
  drift?: string;
}

/** getInstanceStatus reads desired versus observed state, tasks and rollout
 * state. Answers `orchestrator_unavailable` when the cluster is unreachable —
 * reads of the record itself are unaffected, because the database is the source
 * of truth, not the cluster (`SC-F300`). */
export function getInstanceStatus(id: string): Promise<InstanceStatus> {
  return getJSON<InstanceStatus>(`${BASE}/${encodeURIComponent(id)}/status`);
}

/** DatasourceHealth is one datasource's probe result as the instance reports it. */
export interface DatasourceHealth {
  name: string;
  type: string;
  healthy: boolean;
  last_check: string;
  latency_ms: number | null;
  error: string | null;
}

/**
 * InstanceHealth composes what today takes four separate calls to the instance.
 * `notes` is not decoration: readiness is fail-closed at boot and stays false
 * until the first datasource probe, so a 503 window of a few seconds after start
 * is expected and must not be rendered as a failed rollout (`SC-F315`).
 */
export interface InstanceHealth {
  /** This request probed the instance. When false the answer is the stored
   * record and `reachable` says nothing: nobody tried to reach it. */
  live: boolean;
  reachable: boolean;
  rest: { health: string; ready: boolean; checked: string };
  admin: {
    config_digest: string;
    config_version: number;
    version: string;
  } | null;
  datasources: DatasourceHealth[];
  notes: string[];
}

/** getInstanceHealth reads aggregated readiness and datasource health. */
export function getInstanceHealth(id: string): Promise<InstanceHealth> {
  return getJSON<InstanceHealth>(`${BASE}/${encodeURIComponent(id)}/health`);
}

/** InstanceEvent is one row of the lifecycle timeline: created, publishing,
 * published, reload confirmed, restart requested, rollout paused, health
 * changed, reconcile drift detected, quarantined. */
export interface InstanceEvent {
  id: string;
  created: string;
  kind: string;
  message: string;
  /** Never carries secret material (`06` §1.4). */
  metadata?: Record<string, unknown>;
}

/** listInstanceEvents returns the lifecycle event history, newest first. */
export function listInstanceEvents(
  id: string,
  params: PageParams = {},
): Promise<Page<InstanceEvent>> {
  return getJSON<Page<InstanceEvent>>(
    `${BASE}/${encodeURIComponent(id)}/events${query({ ...params })}`,
  );
}

/**
 * getInstanceLogs returns a bounded container log tail (`SC-F320`, v1),
 * credential-stripped. Maintainer level.
 *
 * The `?follow=true` SSE variant is deliberately not wrapped here: `06` §1.9
 * makes it a payload-carrying stream distinct from the job progress channel, and
 * nothing in the UI consumes it yet. Adding it must not become a second progress
 * mechanism (`I7`).
 */
export async function getInstanceLogs(
  id: string,
  params: { tail?: number; since?: string } = {},
): Promise<string> {
  const res = await request(
    `${BASE}/${encodeURIComponent(id)}/logs${query({ ...params })}`,
  );
  return res.text();
}

// ─────────────────────────────────────────────────────── secrets ──

/**
 * SecretEntry is one row of the instance's secret inventory (`06` §3.7).
 * **Values are never returned** — only the logical name, the `secret://`
 * reference the rendered config carries, the materialized target inside the
 * container, and when it was last set.
 */
export interface SecretEntry {
  name: string;
  /** `secret://file//run/secrets/…` or `secret://env/NAME`. The only two forms
   * Sluice's resolver accepts; `vault`, `aws-sm` and `gcp-sm` are rejected at
   * parse, which fails `config validate` and fails boot. */
  ref: string;
  target: string;
  last_set: string;
  /** What references it — a datasource name, a SubjectBinding, a mask salt. */
  used_by: string[];
}

/** listSecrets returns the instance's secret inventory. Maintainer level, and
 * gated on the `instances` feature: it is the instance's *whole* secret
 * inventory, not just datasource credentials. */
export function listSecrets(id: string): Promise<Page<SecretEntry>> {
  return getJSON<Page<SecretEntry>>(
    `${BASE}/${encodeURIComponent(id)}/secrets`,
  );
}

/**
 * putSecret sets or replaces a secret value. **Absent from the scoped-token
 * route map** for the same reason API-key CRUD is: `SC-F205` has no feature for
 * security management, and secret material is exactly that.
 */
export function putSecret(
  id: string,
  name: string,
  value: string,
  opts: { verification?: string } = {},
): Promise<SecretEntry> {
  return sendJSON<SecretEntry>(
    `${BASE}/${encodeURIComponent(id)}/secrets/${encodeURIComponent(name)}`,
    "PUT",
    { value },
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

/** deleteSecret removes a secret. Refused with `409 conflict` while something
 * still references it. */
export function deleteSecret(
  id: string,
  name: string,
  opts: { verification?: string } = {},
): Promise<void> {
  return del(
    `${BASE}/${encodeURIComponent(id)}/secrets/${encodeURIComponent(name)}`,
    {
      verification: opts.verification,
    },
  );
}

/** InstanceJob is a job narrowed to this instance, re-exported so a console tab
 * needs one import. See `api/jobs.ts` for the polling helper. */
export type InstanceJob = Job;

// ──────────────────────────────────────── the image allowlist (SC-F323) ──

/**
 * SluiceImage is one row of the control-plane's own digest allowlist
 * (`02` §11.1's `sluice_images`). The image is **never** tenant-supplied: a
 * create or an upgrade names a *version*, the control plane looks the digest up
 * here, and a version that is absent or revoked is refused with
 * `image_not_allowed` (`SC-F323`, `SC-S320`).
 *
 * `source_commit` is not decoration and must be rendered wherever a version is
 * chosen. `concept.md` §1.3 commits publicly to running an **unmodified upstream
 * build** — the same source tree at a pinned commit, with no Cloud-specific
 * patches — and `02` §11.1 states plainly that without the commit on the
 * allowlist row that sentence has no artifact behind it and cannot be checked by
 * anyone, including us. The commit and the digest are the evidence for the
 * claim, so the UI shows both.
 *
 * TODO(SC-F323): `06` §3.4 specifies no route for this catalogue even though
 * `POST /v1/instances` and `POST /v1/instances/{id}/upgrade` both take a
 * `sluice_version` that only this list can supply, and `SC-S320` requires the
 * allowlist to be readable ("each entry is a full digest published by the
 * control plane, with the Sluice version, release notes link and a
 * supported/deprecated flag"). The path below follows `06` §3.1's spelling for
 * the other control-plane-owned catalogue (`GET /v1/policy-schema`); the field
 * set is `02` §11.1's columns minus the ones that are operations-only. If the
 * handler lands under another path, this is the one line that changes.
 */
export interface SluiceImage {
  /** The Sluice version this digest was built for, e.g. `0.1.0-rc3`. */
  version: string;
  /** `sha256:<64hex>`. What actually runs; the tag is never deployed. */
  digest: string;
  /** The upstream `sluice` commit SHA this digest was built from. */
  source_commit: string;
  channel: "stable" | "candidate" | "security";
  published: string;
  /** Superseded but still runnable. `SC-S320`'s supported/deprecated flag. */
  deprecated: boolean;
  /** Withdrawn — refused at spec-build time, so it must not be offered. */
  revoked: boolean;
  revoked_reason: string | null;
  /** Refuses an upgrade that would skip a version requiring an intermediate
   * step (`02` §11.4). Null when any predecessor may upgrade to it. */
  min_upgrade_from: string | null;
  /** Release notes, shown next to the version in the picker and the upgrade
   * dialog. */
  notes: string;
  notes_url?: string;
}

/** listSluiceVersions returns the published digest allowlist (`SC-F323`),
 * newest first. It is the only source a version picker may draw from. */
export function listSluiceVersions(
  params: { channel?: SluiceImage["channel"] } & PageParams = {},
): Promise<Page<SluiceImage>> {
  return getJSON<Page<SluiceImage>>(
    `/v1/sluice-versions${query({ ...params })}`,
  );
}

// ─────────────────────────────────────────── the size table (SC-F309) ──

/**
 * InstanceSizeClass is one row of the resource table (`02` §3.6). Everything in
 * it is **derived from the size identifier and nothing else** — that is the
 * whole of `SC-F309`. A `duckdb.memoryLimit` above the cgroup limit converts
 * graceful spilling into an OOM kill, and `duckdb.threads` above the CPU quota
 * converts parallelism into throttling, so the UI offers one knob and shows what
 * it derives rather than offering the parts.
 */
export interface InstanceSizeClass {
  size: "small" | "medium" | "large";
  label: string;
  /** vCPU limit, as displayed. */
  cpu: string;
  /** vCPU reservation. Reservations drive scheduling; limits are the cgroup. */
  cpu_reserved: string;
  memory: string;
  memory_reserved: string;
  /** DuckDB's own `memory_limit`, ≈0.7 × (memory − audit buffer). */
  duckdb_memory: string;
  duckdb_threads: number;
  duckdb_max_open: number;
  /**
   * A planning figure for the state directory, not a quota: on k3s state is a
   * subPath of one hostPath volume, which enforces no size (`02` §7.6). There is
   * no spill volume; DuckDB spills into the state directory.
   */
  state_volume: string;
  /** The sustained query rate the class is sized against (`02` §3.6). */
  sustained_queries_per_day: number;
}

/**
 * INSTANCE_SIZES is the customer-facing size table, transcribed from `02` §3.6.
 *
 * TODO(SC-F309): this is a **mirror**, not a source. The table lives in the
 * orchestrator's spec builder, no route publishes it, and `06` §3.4 defines
 * none. It is mirrored here for the same reason `SC-F205`'s feature vocabulary
 * is mirrored in the UI — the picker is unusable without the derived numbers —
 * and it carries the same obligation: a test must assert this table equal to the
 * Go one, or a size change ships a screen that lies about what it provisions.
 *
 * `xlarge` exists in `02` §3.6 and is deliberately absent here: `SC-F309`'s
 * done-when names `small`/`medium`/`large` plus the dedicated-node tiers
 * (`SC-F310`, v1), and [InstanceSize] carries the same three.
 */
export const INSTANCE_SIZES: readonly InstanceSizeClass[] = [
  {
    size: "small",
    label: "Small",
    cpu: "1.0 vCPU",
    cpu_reserved: "0.5 vCPU",
    memory: "2 GiB",
    memory_reserved: "2 GiB",
    duckdb_memory: "1400 MB",
    duckdb_threads: 1,
    duckdb_max_open: 4,
    state_volume: "10 GiB",
    sustained_queries_per_day: 50_000,
  },
  {
    size: "medium",
    label: "Medium",
    cpu: "2.0 vCPU",
    cpu_reserved: "1.0 vCPU",
    memory: "4 GiB",
    memory_reserved: "4 GiB",
    duckdb_memory: "2800 MB",
    duckdb_threads: 2,
    duckdb_max_open: 6,
    state_volume: "40 GiB",
    sustained_queries_per_day: 250_000,
  },
  {
    size: "large",
    label: "Large",
    cpu: "4.0 vCPU",
    cpu_reserved: "2.0 vCPU",
    memory: "8 GiB",
    memory_reserved: "8 GiB",
    duckdb_memory: "5600 MB",
    duckdb_threads: 4,
    duckdb_max_open: 8,
    state_volume: "80 GiB",
    sustained_queries_per_day: 1_000_000,
  },
];

/** sizeClass looks a size up in [INSTANCE_SIZES]. Returns undefined for a
 * dedicated tier (`SC-F310`), whose numbers are per-pool and not in this
 * table — render the identifier rather than inventing a row for it. */
export function sizeClass(size: InstanceSize): InstanceSizeClass | undefined {
  return INSTANCE_SIZES.find((s) => s.size === size);
}

// ──────────────────────────────────────────────────────── realtime ──

/**
 * subscribeInstances asks the realtime channel to tell us that *something*
 * about an instance changed, and nothing more.
 *
 * `05` §1.6 makes this a rule rather than a preference: **realtime is an
 * invalidation signal, never a payload.** The `instances` collection carries
 * read rules only so the SPA can be woken up (`SC-F319`); the record that comes
 * over the socket is auto-REST's projection, whose field-hiding is a separate,
 * hand-maintained obligation, and reading it would make every future column a
 * potential leak. The callback therefore takes no argument by construction: a
 * subscriber refetches through `/v1`, which is the surface with the role checks,
 * the scope checks and the documented shape.
 *
 * Resolves to the unsubscribe function. It rejects if the socket cannot be
 * opened — a caller should treat that as "no live updates", not as a page
 * failure, and keep its manual refresh working.
 */
export function subscribeInstances(onChanged: () => void): Promise<() => void> {
  return pb.collection("instances").subscribe("*", () => onChanged());
}

// ─────────────────────────────────────────── organization defaults ──
//
// `SC-F110`: `GET|PATCH /v1/orgs/{orgId}/instance-defaults`.
//
// The route is organization-scoped and lives in `internal/instances` rather than
// in `internal/accounts`, and the same reason puts these two functions here rather
// than in `api/orgs.ts`: the values are an INSTANCE vocabulary. The size class is
// the same closed set `INSTANCE_SIZES` describes, and it is validated server-side
// against the same parser `POST /v1/instances` uses — so a default this form
// accepts is a default the create route will accept.

/** OrgInstanceDefaults is what the organization has authored, plus the vocabulary
 * a form needs to offer. */
export interface OrgInstanceDefaults {
  /** `node_pools` record id, or "" for "no organization default". */
  node_pool: string;
  /** The pool's display name, resolved server-side. Absent when the id is empty
   * — or when the pool has since been deleted, which the relation deliberately
   * allows: deleting a shared pool must not delete every organization that
   * preferred it. */
  node_pool_name?: string;
  /** One of [sizes], or "" for "no organization default". */
  size: string;
  /** The closed size vocabulary, served rather than hard-coded, so this form and
   * the create wizard cannot offer different lists. */
  sizes: string[];
  /** The sentence stating what these values do and do not do. Rendered verbatim:
   * the server composes it so no client gets it subtly wrong, and the half that
   * matters is that changing a default moves and restarts nothing. */
  applies: string;
}

/** getOrgInstanceDefaults reads the authored defaults. Viewer and above. */
export function getOrgInstanceDefaults(
  orgId: string,
): Promise<OrgInstanceDefaults> {
  return getJSON<OrgInstanceDefaults>(`/v1/orgs/${orgId}/instance-defaults`);
}

/**
 * setOrgInstanceDefaults writes one or both defaults. Owner only.
 *
 * Absent means unchanged and an EXPLICIT empty string clears — pass `""` to drop a
 * default, omit the field to leave it alone. A body carrying neither field is
 * refused rather than answered as a no-op, because on a route whose semantics are
 * "absent means unchanged" an empty body is a misspelled field name.
 */
export function setOrgInstanceDefaults(
  orgId: string,
  patch: { node_pool?: string; size?: string },
): Promise<OrgInstanceDefaults> {
  return sendJSON<OrgInstanceDefaults>(
    `/v1/orgs/${orgId}/instance-defaults`,
    "PATCH",
    patch,
  );
}
